import { logger } from 'modules/logger';
import type { ApolloError } from '@apollo/client';
import type { EnqueueToastFn } from 'common/ui/Toaster';
import type { SlateContent } from '../types';
import { findEnforcedEmptyRequiredFields } from './reportValidation';
import type { SubmissionTarget } from './useSubmissionWorkstationID';

/**
 * Shared submit-time guards/effects used by both the report submit path (useSubmitReport) and the
 * addendum submit path (useAddendumWorkflow.submitAddendum). Extracted from the handleSubmit-local
 * helpers so both owners can share them without cross-hook injection.
 */

/**
 * Returns true if submission should be blocked because required fields are empty (and opens the
 * empty-required-fields dialog). Prelim submissions skip this validation.
 */
export const enforceRequiredFields = ({
  content,
  isPrelim,
  isEmptyRequiredFieldsDialogOpen,
  openEmptyRequiredFieldsDialog,
  setAllRequiredFieldsTouched,
}: {
  content: SlateContent | null;
  isPrelim: boolean;
  isEmptyRequiredFieldsDialogOpen: boolean;
  openEmptyRequiredFieldsDialog: (
    emptyRequiredFields: ReturnType<typeof findEnforcedEmptyRequiredFields>
  ) => void;
  setAllRequiredFieldsTouched: () => void;
}): boolean => {
  if (!isEmptyRequiredFieldsDialogOpen && content) {
    const emptyRequiredFields = findEnforcedEmptyRequiredFields({ content, isPrelim });

    if (emptyRequiredFields.length > 0) {
      openEmptyRequiredFieldsDialog(emptyRequiredFields);
      setAllRequiredFieldsTouched();
      logger.info('[submitGuards] Attempted to submit with empty required fields', {
        count: emptyRequiredFields.length,
      });
      return true;
    }
  }
  return false;
};

export const notifySubmissionCancelled = ({ onError }: { onError?: () => void }): void => {
  onError && onError();
};

export const notifyWorkstationPatchFailure = ({
  enqueueToast,
  target,
  error,
  onError,
}: {
  enqueueToast: EnqueueToastFn;
  target: SubmissionTarget | 'prelim';
  error?: ApolloError;
  onError?: () => void;
}): void => {
  const submissionLabel =
    target === 'addendum' ? 'Addendum' : target === 'prelim' ? 'Preliminary report' : 'Report';
  const reason = error?.networkError ? 'a connection error' : 'an unexpected error';

  enqueueToast(
    `Unable to save the Workstation ID due to ${reason}. ${submissionLabel} was not submitted. Try again.`,
    {
      icon: 'redWarning',
    }
  );
  onError && onError();
};
